// react components
import React, {
    useEffect,
    useRef,
} from 'react'
import L from 'leaflet'
import {
    FeatureGroup,
    Map,
    TileLayer,
} from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-markercluster'
import {
    useDispatch,
    useSelector,
} from 'react-redux'

// data
import {
    defaultReduxState,
    leaflet_attribution_mapbox_site,
    leaflet_url_mapbox_dark_site,
    leaflet_url_mapbox_light_site,
    leaflet_url_mapbox_outdoors_site,
    leaflet_url_mapbox_satellite_site,
    leaflet_url_mapbox_streets_site,
    reduxModalErrorEventHandlerSite,
} from 'data'

// props
type TemplateBlock799LeafletSiteProps = {
    center: {
        lat: number
        lng: number
    }
    icon: string | undefined
    markers?: {
        extraInfo?: any
        lat?: number
        lng?: number
    }[]
    onMarkerClick: (e: any) => void
    stylesMap: {
        leafletClusterColor: string | undefined
        leafletMarkerColor1: string | undefined
        leafletMarkerColor2: string | undefined
        leafletMarkerStyle: string | undefined
        mapProvider: string | undefined
        mapStyles: string | undefined
        mapZoom: string | undefined
    } | undefined
    zoom: number | undefined
}

// main
export const TemplateBlock799LeafletSite: React.FC<TemplateBlock799LeafletSiteProps> = React.memo(({
    center,
    icon,
    markers,
    onMarkerClick,
    stylesMap,
    zoom,
}) => {

    // consts
    const mapStyles: any = {
        'leaflet-dark': leaflet_url_mapbox_dark_site,
        'leaflet-light': leaflet_url_mapbox_light_site,
        'leaflet-outdoor': leaflet_url_mapbox_outdoors_site,
        'leaflet-satellite': leaflet_url_mapbox_satellite_site,
        'leaflet-streets': leaflet_url_mapbox_streets_site,
    }

    const dispatch = useDispatch()

    const clusterRef = useRef(null)
    const groupRef = useRef<FeatureGroup>(null)
    const mapRef = useRef<Map>(null)

    const reduxCacheSiteportfolioid = useSelector((state: defaultReduxState) => state.reduxCacheSite.portfolio?.id)
    const reduxModalSite = useSelector((state: defaultReduxState) => state.reduxModalSite)

    const deviceType = reduxModalSite.deviceType

    useEffect(() => {
        addMarkers(markers)
    }, [
        markers,
    ])

    function createCustomMarkerIcon(color1: string, color2: string, markerStyle?: string) {
        const marker1 = `
        <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 16 24" width="30" height="30">
            <g transform="translate(0 -1028.4)">
                <path fill="${color1}" d="M8,1028.4c-4.4,0-8,3.6-8,8c0,1.4,0.4,2.8,1,3.9c0.1,0.2,0.2,0.4,0.3,0.6l6.6,11.5l6.6-11.5
                    c0.1-0.2,0.2-0.3,0.3-0.5l0.1-0.1c0.6-1.2,1-2.5,1-3.9C16,1032,12.4,1028.4,8,1028.4z M8,1032.4c2.2,0,4,1.8,4,4c0,2.2-1.8,4-4,4
                    c-2.2,0-4-1.8-4-4C4,1034.2,5.8,1032.4,8,1032.4z"/>
                <path fill="${color2}" d="M8,1031.4c-2.8,0-5,2.2-5,5c0,2.8,2.2,5,5,5c2.8,0,5-2.2,5-5C13,1033.6,10.8,1031.4,8,1031.4z M8,1033.4
                    c1.7,0,3,1.3,3,3s-1.3,3-3,3s-3-1.3-3-3S6.3,1033.4,8,1033.4z"/>
            </g>
        </svg>`

        const marker2 = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 446 594" width="30" height="30">
            <path fill="${color1}" d="M220.7,594h4.3c4-.5,7.9-2.2,11-5.3,8.5-8.6,209.7-211.5,209.7-365.9S345.7,0,222.8,0,0,99.9,0,222.8s201.1,357.3,209.7,365.9c3.1,3.1,7,4.8,11,5.3ZM202.6,358.9c-2.2-.3-4.4-.7-6.6-1.2-2.2-.4-4.4-.9-6.6-1.5-2.2-.5-4.4-1.1-6.5-1.8-2.2-.6-4.3-1.4-6.4-2.1-2.1-.8-4.2-1.6-6.3-2.4-2.1-.9-4.1-1.8-6.2-2.7-2-1-4-2-6-3-2-1.1-3.9-2.2-5.9-3.3-1.9-1.2-3.8-2.4-5.7-3.6-1.9-1.3-3.7-2.5-5.5-3.9-1.8-1.3-3.6-2.7-5.3-4.2-1.7-1.4-3.4-2.9-5.1-4.4-1.7-1.5-3.3-3.1-4.9-4.7-1.6-1.6-3.1-3.2-4.7-4.9-1.5-1.7-3-3.4-4.4-5.1-1.4-1.7-2.8-3.5-4.2-5.3-1.3-1.8-2.6-3.6-3.9-5.5-1.3-1.9-2.5-3.8-3.6-5.7-1.2-1.9-2.3-3.9-3.3-5.9-1.1-2-2.1-4-3-6-1-2-1.9-4.1-2.7-6.2-.9-2.1-1.7-4.2-2.4-6.3-.8-2.1-1.5-4.3-2.1-6.4-.6-2.2-1.3-4.3-1.8-6.5-.5-2.2-1-4.4-1.5-6.6-.4-2.2-.8-4.4-1.2-6.7-.3-2.2-.6-4.5-.8-6.7-.2-2.2-.4-4.5-.5-6.7-.1-2.2-.2-4.5-.2-6.7s0-4.5.2-6.8c.1-2.2.3-4.5.5-6.7.2-2.2.5-4.5.8-6.7.3-2.2.7-4.4,1.2-6.7.4-2.2.9-4.4,1.5-6.6.5-2.2,1.2-4.4,1.8-6.5.7-2.2,1.4-4.3,2.1-6.4.8-2.1,1.6-4.2,2.4-6.3.9-2.1,1.8-4.1,2.7-6.2,1-2,2-4,3-6,1.1-2,2.2-3.9,3.3-5.9,1.2-1.9,2.4-3.8,3.6-5.7,1.2-1.9,2.5-3.7,3.9-5.5,1.3-1.8,2.7-3.6,4.2-5.3,1.4-1.7,2.9-3.4,4.4-5.1,1.5-1.7,3.1-3.3,4.7-4.9,1.6-1.6,3.2-3.1,4.9-4.7,1.7-1.5,3.4-3,5.1-4.4,1.7-1.4,3.5-2.8,5.3-4.1,1.8-1.3,3.6-2.6,5.5-3.9,1.9-1.2,3.8-2.5,5.7-3.6,1.9-1.2,3.9-2.3,5.9-3.3,2-1.1,4-2.1,6-3s4.1-1.9,6.2-2.7c2.1-.9,4.2-1.7,6.3-2.4,2.1-.8,4.3-1.5,6.4-2.1,2.2-.6,4.3-1.3,6.5-1.8,2.2-.5,4.4-1,6.6-1.5,2.2-.4,4.4-.8,6.6-1.2,2.2-.3,4.5-.6,6.7-.8,2.2-.2,4.5-.4,6.7-.5,2.3-.1,4.5-.2,6.8-.2s4.5,0,6.7.2c2.3.1,4.5.3,6.7.5,2.2.2,4.5.5,6.7.8,2.2.3,4.4.7,6.6,1.2,2.2.4,4.4.9,6.6,1.5,2.2.5,4.4,1.2,6.5,1.8,2.2.7,4.3,1.4,6.4,2.1,2.1.8,4.2,1.6,6.3,2.4,2.1.9,4.1,1.8,6.2,2.7,2,1,4,2,6,3,2,1.1,3.9,2.2,5.9,3.3,1.9,1.2,3.8,2.4,5.7,3.6,1.9,1.2,3.7,2.5,5.5,3.9,1.8,1.3,3.6,2.7,5.3,4.1,1.7,1.4,3.4,2.9,5.1,4.4,1.7,1.5,3.3,3.1,4.9,4.7,1.6,1.6,3.1,3.2,4.7,4.9,1.5,1.7,3,3.4,4.4,5.1,1.4,1.7,2.8,3.5,4.2,5.3,1.3,1.8,2.6,3.6,3.9,5.5,1.2,1.9,2.5,3.8,3.6,5.7,1.2,1.9,2.3,3.9,3.3,5.9,1.1,2,2.1,4,3,6,1,2,1.9,4.1,2.7,6.2.9,2.1,1.7,4.2,2.4,6.3.8,2.1,1.5,4.3,2.1,6.4.7,2.2,1.3,4.3,1.8,6.5.5,2.2,1,4.4,1.5,6.6.4,2.2.8,4.4,1.2,6.7.3,2.2.6,4.5.8,6.7.2,2.2.4,4.5.5,6.7.1,2.3.2,4.5.2,6.8s0,4.5-.2,6.7c-.1,2.3-.3,4.5-.5,6.7-.2,2.2-.5,4.5-.8,6.7-.3,2.2-.7,4.4-1.2,6.7-.4,2.2-.9,4.4-1.5,6.6-.5,2.2-1.1,4.4-1.8,6.5-.6,2.2-1.4,4.3-2.1,6.4-.8,2.1-1.6,4.2-2.4,6.3-.9,2.1-1.8,4.1-2.7,6.2-1,2-2,4-3,6-1.1,2-2.2,3.9-3.3,5.9-1.2,1.9-2.4,3.8-3.6,5.7-1.3,1.9-2.5,3.7-3.9,5.5-1.3,1.8-2.7,3.6-4.2,5.3-1.4,1.7-2.9,3.4-4.4,5.1-1.5,1.7-3.1,3.3-4.7,4.9-1.6,1.6-3.2,3.1-4.9,4.7-1.7,1.5-3.4,3-5.1,4.4-1.7,1.4-3.5,2.8-5.3,4.2-1.8,1.3-3.6,2.6-5.5,3.9-1.9,1.2-3.8,2.5-5.7,3.6-1.9,1.2-3.9,2.3-5.9,3.3-2,1.1-4,2.1-6,3-2,1-4.1,1.9-6.2,2.7-2.1.9-4.2,1.7-6.3,2.4-2.1.8-4.3,1.5-6.4,2.1-2.2.7-4.3,1.3-6.5,1.8-2.2.5-4.4,1-6.6,1.5-2.2.4-4.4.8-6.6,1.2-2.2.3-4.5.6-6.7.8-2.2.2-4.5.4-6.7.5-2.2.1-4.5.2-6.7.2s-4.5,0-6.8-.2c-2.2-.1-4.5-.3-6.7-.5-2.2-.2-4.5-.5-6.7-.8Z"/>
            <path fill="${color2}" d="M222.8,360.3c2.3,0,4.5,0,6.7-.2,2.3-.1,4.5-.3,6.7-.5,2.2-.2,4.5-.5,6.7-.8,2.2-.3,4.4-.7,6.6-1.2,2.2-.4,4.4-.9,6.6-1.5,2.2-.5,4.4-1.1,6.5-1.8,2.2-.6,4.3-1.4,6.4-2.1,2.1-.8,4.2-1.6,6.3-2.4,2.1-.9,4.1-1.8,6.2-2.7,2-1,4-2,6-3,2-1.1,3.9-2.2,5.9-3.3,1.9-1.2,3.8-2.4,5.7-3.6,1.9-1.3,3.7-2.5,5.5-3.9,1.8-1.3,3.6-2.7,5.3-4.2,1.7-1.4,3.4-2.9,5.1-4.4,1.7-1.5,3.3-3.1,4.9-4.7,1.6-1.6,3.1-3.2,4.7-4.9,1.5-1.7,3-3.4,4.4-5.1,1.4-1.7,2.8-3.5,4.2-5.3,1.3-1.8,2.6-3.6,3.9-5.5,1.2-1.9,2.5-3.8,3.6-5.7,1.2-1.9,2.3-3.9,3.3-5.9,1.1-2,2.1-4,3-6,1-2,1.9-4.1,2.7-6.2.9-2.1,1.7-4.2,2.4-6.3.8-2.1,1.5-4.3,2.1-6.4.7-2.2,1.3-4.3,1.8-6.5.5-2.2,1-4.4,1.5-6.6.4-2.2.8-4.4,1.2-6.7.3-2.2.6-4.5.8-6.7.2-2.2.4-4.5.5-6.7.1-2.2.2-4.5.2-6.7s0-4.5-.2-6.8c-.1-2.2-.3-4.5-.5-6.7-.2-2.2-.5-4.5-.8-6.7-.3-2.2-.7-4.4-1.2-6.7-.4-2.2-.9-4.4-1.5-6.6-.5-2.2-1.1-4.4-1.8-6.5-.6-2.2-1.4-4.3-2.1-6.4-.8-2.1-1.6-4.2-2.4-6.3-.9-2.1-1.8-4.1-2.7-6.2-1-2-2-4-3-6-1.1-2-2.2-3.9-3.3-5.9-1.2-1.9-2.4-3.8-3.6-5.7-1.3-1.9-2.5-3.7-3.9-5.5-1.3-1.8-2.7-3.6-4.2-5.3-1.4-1.7-2.9-3.4-4.4-5.1-1.5-1.7-3.1-3.3-4.7-4.9-1.6-1.6-3.2-3.1-4.9-4.7-1.7-1.5-3.4-3-5.1-4.4-1.7-1.4-3.5-2.8-5.3-4.1-1.8-1.3-3.6-2.6-5.5-3.9-1.9-1.2-3.8-2.5-5.7-3.6-1.9-1.2-3.9-2.3-5.9-3.3-2-1.1-4-2.1-6-3-2-1-4.1-1.9-6.2-2.7-2.1-.9-4.2-1.7-6.3-2.4-2.1-.8-4.3-1.5-6.4-2.1-2.2-.6-4.3-1.3-6.5-1.8-2.2-.5-4.4-1-6.6-1.5-2.2-.4-4.4-.8-6.6-1.2-2.2-.3-4.5-.6-6.7-.8-2.2-.2-4.5-.4-6.7-.5-2.2-.1-4.5-.2-6.7-.2s-4.5,0-6.8.2c-2.2.1-4.5.3-6.7.5-2.2.2-4.5.5-6.7.8-2.2.3-4.4.7-6.6,1.2-2.2.4-4.4.9-6.6,1.5-2.2.5-4.4,1.2-6.5,1.8-2.2.7-4.3,1.4-6.4,2.1-2.1.8-4.2,1.6-6.3,2.4-2.1.9-4.1,1.8-6.2,2.7s-4,2-6,3c-2,1.1-3.9,2.2-5.9,3.3-1.9,1.2-3.8,2.4-5.7,3.6-1.9,1.2-3.7,2.5-5.5,3.9-1.8,1.3-3.6,2.7-5.3,4.1-1.7,1.4-3.4,2.9-5.1,4.4-1.7,1.5-3.3,3.1-4.9,4.7-1.6,1.6-3.1,3.2-4.7,4.9-1.5,1.7-3,3.4-4.4,5.1-1.4,1.7-2.8,3.5-4.2,5.3-1.3,1.8-2.6,3.6-3.9,5.5-1.3,1.9-2.5,3.8-3.6,5.7-1.2,1.9-2.3,3.9-3.3,5.9-1.1,2-2.1,4-3,6-1,2-1.9,4.1-2.7,6.2-.9,2.1-1.7,4.2-2.4,6.3-.8,2.1-1.5,4.3-2.1,6.4-.6,2.2-1.3,4.3-1.8,6.5-.5,2.2-1,4.4-1.5,6.6-.4,2.2-.8,4.4-1.2,6.7-.3,2.2-.6,4.5-.8,6.7-.2,2.2-.4,4.5-.5,6.7-.1,2.3-.2,4.5-.2,6.8s0,4.5.2,6.7c.1,2.3.3,4.5.5,6.7.2,2.2.5,4.5.8,6.7.3,2.2.7,4.4,1.2,6.7.4,2.2.9,4.4,1.5,6.6.5,2.2,1.2,4.4,1.8,6.5.7,2.2,1.4,4.3,2.1,6.4.8,2.1,1.6,4.2,2.4,6.3.9,2.1,1.8,4.1,2.7,6.2,1,2,2,4,3,6,1.1,2,2.2,3.9,3.3,5.9,1.2,1.9,2.4,3.8,3.6,5.7,1.2,1.9,2.5,3.7,3.9,5.5,1.3,1.8,2.7,3.6,4.2,5.3,1.4,1.7,2.9,3.4,4.4,5.1,1.5,1.7,3.1,3.3,4.7,4.9,1.6,1.6,3.2,3.1,4.9,4.7,1.7,1.5,3.4,3,5.1,4.4,1.7,1.4,3.5,2.8,5.3,4.2,1.8,1.3,3.6,2.6,5.5,3.9,1.9,1.2,3.8,2.5,5.7,3.6,1.9,1.2,3.9,2.3,5.9,3.3,2,1.1,4,2.1,6,3,2,1,4.1,1.9,6.2,2.7,2.1.9,4.2,1.7,6.3,2.4,2.1.8,4.3,1.5,6.4,2.1,2.2.7,4.3,1.3,6.5,1.8,2.2.5,4.4,1,6.6,1.5,2.2.4,4.4.8,6.6,1.2,2.2.3,4.5.6,6.7.8,2.2.2,4.5.4,6.7.5,2.3.1,4.5.2,6.8.2ZM150.4,219.2c0-1.2.1-2.4.3-3.5.1-1.2.3-2.4.4-3.5.2-1.2.4-2.3.6-3.5.2-1.2.5-2.3.8-3.5.3-1.2.6-2.3.9-3.4.3-1.1.7-2.3,1.1-3.4.4-1.1.8-2.2,1.3-3.3.5-1.1.9-2.2,1.4-3.3.5-1.1,1-2.1,1.6-3.2.6-1,1.1-2.1,1.8-3.1s1.2-2,1.9-3c.7-1,1.3-2,2-2.9.7-1,1.4-1.9,2.2-2.8.8-.9,1.5-1.8,2.3-2.7.8-.9,1.6-1.7,2.5-2.6.8-.8,1.7-1.7,2.6-2.5.9-.8,1.8-1.6,2.7-2.3s1.9-1.5,2.8-2.2c1-.7,1.9-1.4,2.9-2.1,1-.7,2-1.3,3-1.9s2.1-1.2,3.1-1.8c1-.6,2.1-1.1,3.2-1.6,1.1-.5,2.2-1,3.3-1.4,1.1-.5,2.2-.9,3.3-1.3,1.1-.4,2.2-.8,3.4-1.1,1.1-.3,2.3-.7,3.4-.9,1.1-.3,2.3-.5,3.5-.8,1.2-.2,2.3-.4,3.5-.6,1.2-.2,2.4-.3,3.5-.4,1.2-.1,2.4-.2,3.5-.3,1.2,0,2.4,0,3.6,0s2.4,0,3.6,0c1.2,0,2.4.1,3.5.3,1.2.1,2.4.3,3.5.4s2.3.4,3.5.6c1.2.2,2.3.5,3.5.8,1.1.3,2.3.6,3.4.9,1.1.3,2.3.7,3.4,1.1,1.1.4,2.2.8,3.3,1.3,1.1.5,2.2.9,3.3,1.4,1.1.5,2.1,1,3.2,1.6,1,.6,2.1,1.1,3.1,1.8,1,.6,2,1.2,3,1.9,1,.7,2,1.3,2.9,2.1,1,.7,1.9,1.4,2.8,2.2.9.8,1.8,1.5,2.7,2.3.9.8,1.7,1.6,2.6,2.5.8.8,1.7,1.7,2.5,2.6.8.9,1.6,1.8,2.3,2.7.8.9,1.5,1.9,2.2,2.8.7,1,1.4,1.9,2.1,2.9.7,1,1.3,2,1.9,3s1.2,2.1,1.8,3.1c.6,1,1.1,2.1,1.6,3.2.5,1.1,1,2.2,1.4,3.3.5,1.1.9,2.2,1.3,3.3.4,1.1.8,2.2,1.1,3.4.3,1.1.7,2.3.9,3.4.3,1.1.5,2.3.8,3.5.2,1.2.4,2.3.6,3.5.2,1.2.3,2.3.4,3.5.1,1.2.2,2.4.3,3.5,0,1.2,0,2.4,0,3.6s0,2.4,0,3.6c0,1.2-.1,2.4-.3,3.5-.1,1.2-.3,2.4-.4,3.5-.2,1.2-.4,2.3-.6,3.5-.2,1.2-.5,2.3-.8,3.5-.3,1.1-.6,2.3-.9,3.4-.3,1.1-.7,2.3-1.1,3.4-.4,1.1-.8,2.2-1.3,3.3-.5,1.1-.9,2.2-1.4,3.3-.5,1.1-1,2.1-1.6,3.2-.6,1-1.1,2.1-1.8,3.1-.6,1-1.2,2-1.9,3-.7,1-1.3,2-2.1,2.9-.7,1-1.4,1.9-2.2,2.8-.8.9-1.5,1.8-2.3,2.7-.8.9-1.6,1.7-2.5,2.6-.8.8-1.7,1.7-2.6,2.5-.9.8-1.8,1.6-2.7,2.3-.9.8-1.9,1.5-2.8,2.2-1,.7-1.9,1.4-2.9,2.1-1,.7-2,1.3-3,1.9-1,.6-2.1,1.2-3.1,1.8-1,.6-2.1,1.1-3.2,1.6-1.1.5-2.2,1-3.3,1.4-1.1.5-2.2.9-3.3,1.3-1.1.4-2.2.8-3.4,1.1-1.1.3-2.3.7-3.4.9-1.2.3-2.3.5-3.5.8-1.2.2-2.3.4-3.5.6s-2.3.3-3.5.4c-1.2.1-2.4.2-3.5.3-1.2,0-2.4,0-3.6,0s-2.4,0-3.6,0c-1.2,0-2.4-.1-3.5-.3-1.2-.1-2.4-.3-3.5-.4-1.2-.2-2.3-.4-3.5-.6-1.2-.2-2.3-.5-3.5-.8-1.2-.3-2.3-.6-3.4-.9-1.1-.3-2.3-.7-3.4-1.1-1.1-.4-2.2-.8-3.3-1.3-1.1-.5-2.2-.9-3.3-1.4-1.1-.5-2.1-1-3.2-1.6-1-.6-2.1-1.1-3.1-1.8s-2-1.2-3-1.9c-1-.7-2-1.3-2.9-2.1-1-.7-1.9-1.4-2.8-2.2s-1.8-1.5-2.7-2.3c-.9-.8-1.7-1.6-2.6-2.5-.8-.8-1.7-1.7-2.5-2.6-.8-.9-1.6-1.8-2.3-2.7-.8-.9-1.5-1.9-2.2-2.8-.7-1-1.4-1.9-2-2.9-.7-1-1.3-2-1.9-3-.6-1-1.2-2.1-1.8-3.1-.6-1-1.1-2.1-1.6-3.2-.5-1.1-1-2.2-1.4-3.3-.5-1.1-.9-2.2-1.3-3.3-.4-1.1-.8-2.2-1.1-3.4-.3-1.1-.7-2.3-.9-3.4-.3-1.2-.5-2.3-.8-3.5-.2-1.2-.4-2.3-.6-3.5-.2-1.2-.3-2.3-.4-3.5-.1-1.2-.2-2.4-.3-3.5,0-1.2,0-2.4,0-3.6s0-2.4,0-3.6Z"/>
        </svg>`
        const makerDict: any = {
            marker1: marker1,
            marker2: marker2,
        }
        return L.divIcon({
            className: '',
            html: `<div style="width:30px; height:30px;">${makerDict[markerStyle!] || marker1}</div>`,
            iconSize: [30, 30],
            iconAnchor: [15, 30],
        })
    }

    const customMarker = icon ? L.icon({
        iconUrl: icon,
        iconSize: [30, 30],
        iconAnchor: [15, 30],
    }) : createCustomMarkerIcon(stylesMap?.leafletMarkerColor1 || '#00FFFC', stylesMap?.leafletMarkerColor2 || '#00DBD5', stylesMap?.leafletMarkerStyle)

    function addMarkers(data: {
        extraInfo?: any
        lat?: number
        lng?: number
    }[] | undefined) {
        try {
            if (clusterRef?.current) {
                // @ts-ignore
                clusterRef.current?.leafletElement.clearLayers()
            }
            const markers = L.markerClusterGroup()
            let hasMarkers = false
            data?.map((val) => {
                if (val.lat && val.lng) {
                    hasMarkers = true
                    let customMarker2 = undefined
                    if (val.extraInfo?.icon) {
                        customMarker2 = L.icon({
                            iconUrl: val.extraInfo.icon,
                            iconSize: [30, 30],
                            iconAnchor: [15, 30],
                        })
                    } else if (val.extraInfo?.icon_style) {
                        customMarker2 = createCustomMarkerIcon(
                            val.extraInfo.icon_style.color1 || '#00FFFC',
                            val.extraInfo.icon_style.color2 || '#00DBD5',
                            val.extraInfo.icon_style.style,
                        )
                    }
                    const marker = L.marker(
                        [val.lat, val.lng],
                        {
                            icon: customMarker2 || customMarker,
                        },
                    ).on(
                        'click',
                        () => onMarkerClick(val),
                    )
                    if (val.extraInfo?.text) {
                        marker.bindTooltip(val.extraInfo.text, {
                            permanent: false,
                            direction: 'top',
                            offset: L.point(0, -20),
                            opacity: 0.9,
                        })
                    }
                    // @ts-ignore
                    marker.mo_id = val.parent
                    markers.addLayer(marker)
                }
                return false
            })
            // @ts-ignore
            clusterRef?.current?.leafletElement?.addLayers(markers)
            if (hasMarkers) handleFitBounds(data)
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'TemplateBlock799LeafletSite',
                'addMarkers',
            ))
        }
    }

    function handleFitBounds(data: {
        extraInfo?: any
        lat?: number
        lng?: number
    }[] | undefined) {
        try {
            if (data?.length! > 0) {
                const map = mapRef?.current?.leafletElement
                const group = groupRef?.current?.leafletElement
                if (group) {
                    if (reduxCacheSiteportfolioid === 678) {  // TODO 
                        map?.fitBounds(group.getBounds(), {
                            padding: [50, 50]  // pixels to pad on each side
                        })
                    } else {
                        map?.fitBounds(group.getBounds())
                    }
                }
            }
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'TemplateBlock799LeafletSite',
                'handleFitBounds',
            ))
        }
    }

    const createClusterCustomIcon = (cluster: any) => {
        const count = cluster.getChildCount()
        const color = stylesMap?.leafletClusterColor || 'rgba(0, 150, 255, 0.6)'
        return L.divIcon({
            html: `<div style="
            align-items: center; 
            background: ${color}; 
            border-radius: 50%; 
            box-shadow: 0px 0px 10px rgba(0,0,0,0.5);
            color: white;
            display: flex; 
            font-size: 16px;
            font-weight: bold;
            height: 40px; 
            justify-content: center;
            width: 40px; 
        ">${count}</div>`,
            className: 'custom-cluster-icon'
        })
    }

    return (
        <Map
            boxZoom
            center={center}
            minZoom={1}
            preferCanvas
            ref={mapRef}
            scrollWheelZoom={false}
            zoom={zoom}
            zoomControl={deviceType === 'is-web'}
        >
            <TileLayer
                attribution={leaflet_attribution_mapbox_site}
                tileSize={512}
                url={mapStyles[stylesMap?.mapStyles!] || leaflet_url_mapbox_light_site}
                zoomOffset={-1}
            />
            <FeatureGroup ref={groupRef}>
                <MarkerClusterGroup
                    chunkedLoading
                    iconCreateFunction={createClusterCustomIcon}
                    maxClusterRadius={40}
                    onMarkerClick={onMarkerClick}
                    ref={clusterRef}
                    removeOutsideVisibleBounds
                />
            </FeatureGroup>
        </Map>
    )
})

// //Example values of min & max latlng values
// var lat_min = 1.3049337
// var lat_max = 1.3053515
// var lng_min = 103.2103116
// var lng_max = 103.8400188

// map.setCenter(new google.maps.LatLng(
//   ((lat_max + lat_min) / 2.0),
//   ((lng_max + lng_min) / 2.0)
// ))
// map.fitBounds(new google.maps.LatLngBounds(
//   //bottom left
//   new google.maps.LatLng(lat_min, lng_min),
//   //top right
//   new google.maps.LatLng(lat_max, lng_max)
// ))
